import type { MdsCatalog } from 'types/search-page'

type FakeCatalog = MdsCatalog & {
  is_favorite: boolean
  categories: string[]
  description: string
  track_count: number
}

// PROTOTYPE: demo data so the picker looks like it would with thousands of MDS
// catalogs. Ids are made up and don't exist on the backend.
const NAMES = [
  'Warner Chappell Production Music',
  'Extreme Music',
  'APM Music',
  'Universal Production Music',
  'BMG Production Music',
  'Audio Network',
  'Musicbed',
  'Artlist',
  'Epidemic Sound',
  'Sony Music Publishing',
  'Kobalt Music',
  'Concord Music Publishing',
  'Downtown Music Publishing',
  'Big Machine Label Group',
  'Secretly Group',
  'Sub Pop Records',
  'Domino Recording Company',
  'Beggars Group',
  'Warp Records',
  'Ninja Tune',
  'Merge Records',
  'Matador Records',
  'Rough Trade Records',
  'XL Recordings',
  '4AD',
  'Partisan Records',
  'Dead Oceans',
  'Jagjaguwar',
  'Ghostly International',
  'Stones Throw Records',
  'Epitaph Records',
  'Fueled By Ramen',
  'Nettwerk Music Group',
  'Believe',
  'Empire Distribution',
  'CD Baby',
  'DistroKid Artists',
  'Position Music',
  'Songtradr',
  'Marmoset',
  'Musicbox',
  'Heavy Hitters Music',
  'Elias Music Library',
  'Associated Production Music',
  'FirstCom Music',
  'Killer Tracks',
  'Bleeding Fingers Music',
  'Big Sync Music',
  'West One Music Group',
  'Cavendish Music',
  'Atom Music Audio',
  'Brand X Music',
  'Two Steps From Hell',
  'Immediate Music',
  'Audiomachine',
  'Hans Zimmer Productions',
  'Mute Records',
  'Sacred Bones Records',
  'Big Dada',
  'Anti- Records',
  'Polyvinyl Record Co.',
  'Saddle Creek',
  'Thirty Tigers',
  'New West Records',
  'Rhino Entertainment',
  'Nonesuch Records',
  'Blue Note Records',
  'Verve Records',
  'Impulse! Records',
  'Deutsche Grammophon',
  'Hyperion Records',
  'Naxos Music Library',
  'Mad Decent',
  'Monstercat',
  'Anjunabeats',
  'Defected Records',
  'Spinnin’ Records',
  'Ultra Music',
  'Fool’s Gold Records',
  'Top Dawg Entertainment',
  'Quality Control Music',
  'Mass Appeal Records',
  'Rhymesayers Entertainment',
  'Dualtone Records',
  'Rounder Records',
  'Light in the Attic',
  'Numero Group',
  'Daptone Records',
  'Colemine Records',
  'Big Crown Records',
]

const FAVORITES = new Set([
  'Warner Chappell Production Music',
  'Extreme Music',
  'Musicbed',
  'Position Music',
  'Big Sync Music',
  'Secretly Group',
])

// Mirrors Discover Music's catalog sets: one catalog can be in several.
const CATEGORY_MEMBERS: Record<string, string[]> = {
  Vintage: [
    'Blue Note Records',
    'Verve Records',
    'Impulse! Records',
    'Rhino Entertainment',
    'Light in the Attic',
    'Numero Group',
    'Daptone Records',
    'Colemine Records',
    'Big Crown Records',
    'Nonesuch Records',
    'Deutsche Grammophon',
    'Hyperion Records',
    'Rounder Records',
    'Associated Production Music',
    'Killer Tracks',
  ],
  Independent: [
    'Big Machine Label Group',
    'Secretly Group',
    'Sub Pop Records',
    'Domino Recording Company',
    'Beggars Group',
    'Warp Records',
    'Ninja Tune',
    'Merge Records',
    'Matador Records',
    'Rough Trade Records',
    'XL Recordings',
    '4AD',
    'Partisan Records',
    'Dead Oceans',
    'Jagjaguwar',
    'Ghostly International',
    'Stones Throw Records',
    'Epitaph Records',
    'Nettwerk Music Group',
    'Mute Records',
    'Sacred Bones Records',
    'Big Dada',
    'Anti- Records',
    'Polyvinyl Record Co.',
    'Saddle Creek',
    'Thirty Tigers',
    'New West Records',
    'Dualtone Records',
    'Rounder Records',
    'Light in the Attic',
    'Numero Group',
    'Daptone Records',
    'Colemine Records',
    'Big Crown Records',
    'Mad Decent',
    'Monstercat',
    'Anjunabeats',
    'Defected Records',
    'Fool’s Gold Records',
    'Rhymesayers Entertainment',
    'Mass Appeal Records',
    'Top Dawg Entertainment',
  ],
  // Catalogs based outside the US.
  Worldwide: [
    'Audio Network',
    'West One Music Group',
    'Cavendish Music',
    'Epidemic Sound',
    'Artlist',
    'BMG Production Music',
    'Kobalt Music',
    'Domino Recording Company',
    'Beggars Group',
    'Warp Records',
    'Ninja Tune',
    'Rough Trade Records',
    'XL Recordings',
    '4AD',
    'Big Dada',
    'Mute Records',
    'Defected Records',
    'Spinnin’ Records',
    'Anjunabeats',
    'Monstercat',
    'Nettwerk Music Group',
    'Believe',
    'Deutsche Grammophon',
    'Hyperion Records',
    'Naxos Music Library',
  ],
  'Trailer Music': [
    'Bleeding Fingers Music',
    'Atom Music Audio',
    'Brand X Music',
    'Two Steps From Hell',
    'Immediate Music',
    'Audiomachine',
    'Hans Zimmer Productions',
    'Position Music',
    'Extreme Music',
  ],
  'One-stop': [
    'Musicbed',
    'Artlist',
    'Epidemic Sound',
    'Position Music',
    'Songtradr',
    'Marmoset',
    'Musicbox',
    'Big Sync Music',
    'Heavy Hitters Music',
    'Secretly Group',
    'Domino Recording Company',
    'Ninja Tune',
    'Merge Records',
    'Sub Pop Records',
    'Nettwerk Music Group',
    'Thirty Tigers',
  ],
  'Production Music': [
    'Warner Chappell Production Music',
    'Extreme Music',
    'APM Music',
    'Universal Production Music',
    'BMG Production Music',
    'Audio Network',
    'West One Music Group',
    'Cavendish Music',
    'FirstCom Music',
    'Killer Tracks',
    'Associated Production Music',
    'Elias Music Library',
    'Naxos Music Library',
    'Big Sync Music',
    'Heavy Hitters Music',
  ],
}

export const FAKE_CATEGORIES = Object.keys(CATEGORY_MEMBERS)

const curatedCategoriesFor = (name: string): string[] =>
  FAKE_CATEGORIES.filter(category => CATEGORY_MEMBERS[category].includes(name))

const PRIMARY_TARGET = Math.ceil(NAMES.length / FAKE_CATEGORIES.length)

// Second categories stop a little above the target so the counts stay close.
const SECOND_CATEGORY_CAP = PRIMARY_TARGET + 3

/**
 * Spreads the catalogs evenly so every category has a similar count and every
 * catalog has one. Each catalog takes its least-filled curated category while
 * that is under the target (else the least-filled overall), then keeps a
 * second curated one while that category has room, so some sit in two.
 */
const assignCategories = (): Map<string, string[]> => {
  const counts = new Map(FAKE_CATEGORIES.map(category => [category, 0]))
  const leastFilled = (options: string[]) =>
    options.reduce((best, option) =>
      (counts.get(option) ?? 0) < (counts.get(best) ?? 0) ? option : best,
    )
  const assigned = new Map<string, string[]>()
  NAMES.forEach(name => {
    const curated = curatedCategoriesFor(name)
    const preferred = curated.length ? leastFilled(curated) : null
    // A full preferred category gives the catalog to the emptiest one instead.
    const primary =
      preferred && (counts.get(preferred) ?? 0) < PRIMARY_TARGET
        ? preferred
        : leastFilled(FAKE_CATEGORIES)
    counts.set(primary, (counts.get(primary) ?? 0) + 1)
    assigned.set(name, [primary])
  })
  NAMES.forEach(name => {
    const extras = curatedCategoriesFor(name).filter(
      category => !assigned.get(name)?.includes(category),
    )
    if (!extras.length) return
    const second = leastFilled(extras)
    if ((counts.get(second) ?? 0) >= SECOND_CATEGORY_CAP) return
    counts.set(second, (counts.get(second) ?? 0) + 1)
    assigned.get(name)?.push(second)
  })
  return assigned
}

const ASSIGNED_CATEGORIES = assignCategories()

const CATEGORY_BLURBS: Record<string, string> = {
  Vintage: 'Classic recordings and reissues with an authentic period sound.',
  Independent: 'Artist-driven releases from an independent label.',
  Worldwide: 'Music from outside the US, cleared for global use.',
  'Trailer Music': 'Big, cinematic cues built for trailers and promos.',
  'One-stop': 'Master and publishing cleared together for fast licensing.',
  'Production Music': 'Library music written and cleared for picture.',
}

// Deterministic, so counts don't change between reloads of the demo.
const fakeTrackCount = (index: number): number =>
  ((index * 7919) % 48000) + 1200

const LOGO_COLORS = [
  ['#1f2937', '#f59e0b'],
  ['#7c3aed', '#f5f3ff'],
  ['#0f766e', '#ccfbf1'],
  ['#b91c1c', '#fee2e2'],
  ['#1d4ed8', '#dbeafe'],
  ['#111827', '#f9fafb'],
  ['#c2410c', '#ffedd5'],
  ['#be185d', '#fce7f3'],
  ['#4d7c0f', '#ecfccb'],
  ['#0e7490', '#cffafe'],
]

// Simple marks drawn on a 40x40 tile; none use letters so they read as logos.
const LOGO_MARKS = [
  (fg: string) => `<circle cx="20" cy="20" r="11" fill="${fg}"/>`,
  (fg: string) =>
    `<circle cx="20" cy="20" r="11" fill="none" stroke="${fg}" stroke-width="4"/>`,
  (fg: string) => `<path d="M20 8 L32 31 H8 Z" fill="${fg}"/>`,
  (fg: string) =>
    [8, 14, 20, 26, 32]
      .map(
        (x, i) =>
          `<rect x="${x - 2}" y="${
            20 - [5, 10, 14, 8, 4][i]
          }" width="4" height="${
            [5, 10, 14, 8, 4][i] * 2
          }" rx="2" fill="${fg}"/>`,
      )
      .join(''),
  (fg: string) =>
    `<rect x="10" y="10" width="20" height="20" rx="3" fill="none" stroke="${fg}" stroke-width="4" transform="rotate(45 20 20)"/>`,
  (fg: string) =>
    `<path d="M6 22 Q13 8 20 20 T34 18" fill="none" stroke="${fg}" stroke-width="4" stroke-linecap="round"/>`,
  (fg: string) =>
    `<circle cx="14" cy="20" r="7" fill="${fg}"/><circle cx="26" cy="20" r="7" fill="none" stroke="${fg}" stroke-width="3"/>`,
  (fg: string) =>
    `<rect x="9" y="9" width="9" height="9" fill="${fg}"/><rect x="22" y="22" width="9" height="9" fill="${fg}"/><rect x="22" y="9" width="9" height="9" fill="none" stroke="${fg}" stroke-width="2"/>`,
]

/** Inline SVG so the demo needs no network and no real brand artwork. */
const fakeLogoUrl = (index: number): string => {
  const [bg, fg] = LOGO_COLORS[index % LOGO_COLORS.length]
  // Steps through marks at a different rate than colors so pairs rarely repeat.
  const mark = LOGO_MARKS[(index * 3) % LOGO_MARKS.length](fg)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="${bg}"/>${mark}</svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

const describe = (name: string, categories: string[]): string =>
  [
    `${name} on DISCO.`,
    ...categories.map(category => CATEGORY_BLURBS[category]),
  ].join(' ')

export const FAKE_CATALOGS: FakeCatalog[] = NAMES.map((name, index) => ({
  id: 900000 + index,
  display_name: name,
  image_logo_url: fakeLogoUrl(index),
  is_favorite: FAVORITES.has(name),
  categories: FAKE_CATEGORIES.filter(category =>
    ASSIGNED_CATEGORIES.get(name)?.includes(category),
  ),
  description: describe(
    name,
    FAKE_CATEGORIES.filter(category =>
      ASSIGNED_CATEGORIES.get(name)?.includes(category),
    ),
  ),
  track_count: fakeTrackCount(index),
}))

/**
 * The saved selection keeps only id, name and logo, so this restores the rest
 * (logo, categories, description) when the picker reopens.
 */
export const findFakeCatalog = (
  id: MdsCatalog['id'],
): FakeCatalog | undefined => FAKE_CATALOGS.find(c => c.id === id)

/** Stars stay for the page session only, like a real favorite would until reload. */
export const setFakeFavorite = (id: MdsCatalog['id'], isFavorite: boolean) => {
  const catalog = FAKE_CATALOGS.find(c => c.id === id)
  if (catalog) catalog.is_favorite = isFavorite
}

/**
 * Stands in for the discovery panel list endpoint, with the same filter and
 * page size. Synchronous so the demo never shows a spinner. The query also
 * matches category names, so "vintage" finds every vintage catalog.
 */
export const filterFakeCatalogs = (
  query: string,
  favoritesOnly: boolean,
  category: string | null,
  pageSize: number,
): FakeCatalog[] => {
  const needle = query.trim().toLowerCase()
  const matches = FAKE_CATALOGS.filter(
    catalog =>
      (!favoritesOnly || catalog.is_favorite) &&
      (!category || catalog.categories.includes(category)) &&
      (!needle ||
        catalog.display_name?.toLowerCase().includes(needle) ||
        catalog.categories.some(c => c.toLowerCase().includes(needle))),
  )
  return matches.slice(0, pageSize)
}
